import React from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ResponsiveBar } from '@nivo/bar';
import { BRAND } from '../../theme/brandTheme';

const OUTCOME_COLORS = {
  Kazandı: '#70AD47',
  Kaybetti: '#C00000',
  Etkisiz: '#7F7F7F',
};

const barTheme = {
  axis: {
    ticks: { text: { fill: '#666', fontSize: 11 } },
    legend: { text: { fill: '#666', fontSize: 12 } },
  },
  grid: { line: { stroke: '#eee' } },
  tooltip: {
    container: {
      background: '#fff',
      border: '1px solid #ccc',
      borderRadius: 4,
      fontSize: 12,
    },
  },
};

const TaxonomyBarChart = ({ data }) => {
  if (!data?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Taxonomy verisi yok — raporlara sayfa / değişiklik tipi ekleyin.
      </Typography>
    );
  }

  return (
    <Box sx={{ height: Math.max(220, data.length * 36) }}>
      <ResponsiveBar
        data={data}
        keys={['Kazandı', 'Kaybetti', 'Etkisiz']}
        indexBy="label"
        layout="horizontal"
        margin={{ top: 8, right: 24, bottom: 40, left: 110 }}
        padding={0.35}
        colors={({ id }) => OUTCOME_COLORS[id] || '#999'}
        borderRadius={2}
        axisBottom={{ tickSize: 0 }}
        axisLeft={{ tickSize: 0 }}
        enableLabel={false}
        legends={[
          {
            dataFrom: 'keys',
            anchor: 'bottom',
            direction: 'row',
            translateY: 36,
            itemWidth: 72,
            itemHeight: 16,
            symbolSize: 10,
          },
        ]}
        theme={barTheme}
        tooltip={({ id, value, indexValue, data: row }) => (
          <div style={{ padding: '6px 10px' }}>
            <strong>{indexValue}</strong>
            <br />
            {id}: {value} · Win rate: %{row.winRate} ({row.total} test)
          </div>
        )}
      />
    </Box>
  );
};

/** Faz 2 — taxonomy win rates + monthly outcome stacked bars */
const StatisticsDeepCharts = ({ charts }) => {
  if (!charts) return null;

  const { taxonomyPageType = [], taxonomyChangeType = [], monthlyOutcomes = [] } = charts;
  const hasMonthly = monthlyOutcomes.some(
    (m) => m.Kazandı > 0 || m.Kaybetti > 0 || m.Etkisiz > 0,
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.purple, mb: 2 }}>
        CRO Derinlik Analizi
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <CategoryIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                Sayfa Tipi — Sonuç Dağılımı
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Win rate etiket üzerinde (tooltip)
              </Typography>
              <TaxonomyBarChart data={taxonomyPageType} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <CategoryIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                Değişiklik Tipi — Sonuç Dağılımı
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                CTA, copy, layout vb. kazanma oranları
              </Typography>
              <TaxonomyBarChart data={taxonomyChangeType} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            <CalendarMonthIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
            Aylık Test Sonuçları
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Kazanan / kaybeden / etkisiz test sayısı (sonuçlanan testler, ay bazlı)
          </Typography>
          {!hasMonthly ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Bu dönemde sonuçlanmış test yok.
            </Typography>
          ) : (
            <Box sx={{ height: 240 }}>
              <ResponsiveBar
                data={monthlyOutcomes}
                keys={['Kazandı', 'Kaybetti', 'Etkisiz']}
                indexBy="month"
                margin={{ top: 10, right: 10, bottom: 50, left: 40 }}
                padding={0.3}
                groupMode="stacked"
                colors={({ id }) => OUTCOME_COLORS[id] || '#999'}
                borderRadius={2}
                axisBottom={{ tickRotation: -25 }}
                theme={barTheme}
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom',
                    direction: 'row',
                    translateY: 44,
                    itemWidth: 72,
                    itemHeight: 16,
                    symbolSize: 10,
                  },
                ]}
                tooltip={({ id, value, indexValue }) => (
                  <div style={{ padding: '6px 10px' }}>
                    <strong>{indexValue}</strong> — {id}: {value}
                  </div>
                )}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StatisticsDeepCharts;
